import { isMapId, mapInfo } from "@69ly/maps";
import { NOTES_PER_PAGE, type MatchNote, type NoteFilter, type NoteSide } from "@69ly/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useMatchNotes } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";
import { mapIconUrl } from "../lib/map-art.ts";
import { noteWhen, NotePlayers, NoteTags } from "./NoteFields.tsx";

const mapName = (map: string) => (isMapId(map) ? mapInfo(map).name : map);
/** Where a note opens: its round, a second before its moment. */
const noteUrl = (n: MatchNote) => `/demos/${n.demoId}?r=${n.round + 1}${n.seconds !== null ? `&t=${Math.max(0, n.seconds - 1).toFixed(1)}` : `&tk=${n.tick}`}`;

/** The filters as the address holds them: `?map=&r=3-7&side=t&team=&player=&by=&q=&page=2`. */
function readQuery(p: URLSearchParams): Omit<NoteFilter, "side"> & { side?: NoteSide; page: number; per: number } {
  const [from, to] = (p.get("r") ?? "").split("-").map((x) => (x.trim() === "" ? undefined : Number(x)));
  const ok = (n: number | undefined) => (n !== undefined && Number.isInteger(n) && n >= 1 && n <= 100 ? n : undefined);
  const side = p.get("side");
  const page = Number(p.get("page") ?? "1");
  return {
    map: p.get("map") || undefined,
    round: ok(from),
    roundTo: ok(to),
    side: side === "t" || side === "ct" ? side : undefined,
    team: p.get("team") || undefined,
    player: p.get("player") || undefined,
    by: p.get("by") || undefined,
    q: p.get("q") || undefined,
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    per: NOTES_PER_PAGE,
  };
}

/** The page numbers to offer: the first, the last, and the ones around the current. */
function pageNumbers(page: number, pages: number): (number | null)[] {
  const keep = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
  const out: (number | null)[] = [];
  let last = 0;
  for (const n of [...keep].sort((a, b) => a - b)) {
    if (n - last > 1) out.push(null);
    out.push(n);
    last = n;
  }
  return out;
}

/**
 * The match page's Notes tab: every note of the match, 20 a page, filtered by map, rounds,
 * side, team, player, author and words (the viewer's list filters the same way). The
 * filters and the page are in the address, so a link or a reload opens the same list.
 */
export function NotesBrowser({ matchId }: { matchId: string }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const query = readQuery(params);
  const notes = useMatchNotes(matchId, query);
  // Words are typed into the address a moment after the last key.
  const [q, setQ] = useState(query.q ?? "");
  useEffect(() => {
    if (q.trim() === (query.q ?? "")) return;
    const id = window.setTimeout(() => set({ q: q.trim() }), 300);
    return () => window.clearTimeout(id);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Changes filters (back to page 1) or the page. */
  function set(change: Record<string, string | undefined>) {
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(change)) {
          if (v) n.set(k, v);
          else n.delete(k);
        }
        if (!("page" in change)) n.delete("page");
        return n;
      },
      { replace: true },
    );
  }
  const setRounds = (from: string, to: string) => set({ r: from || to ? `${from}-${to}` : undefined });

  if (notes.isPending) return <Loading />;
  if (notes.error) return <ErrorState message={errorText(t, notes.error)} onRetry={() => void notes.refetch()} />;
  const data = notes.data;
  if (data.all === 0) return <p className="muted">{t("matches.notes.empty")}</p>;
  const { options } = data;
  const filtered = !!(query.map || query.round || query.roundTo || query.side || query.team || query.player || query.by || query.q);
  const pages = Math.max(1, Math.ceil(data.total / data.per));
  const first = (data.page - 1) * data.per;
  const manyMaps = options.maps.length > 1;
  const any = (label: string) => ({ value: "", label });

  return (
    <div className="notes-browser">
      <div className="notes-filters-row" role="group" aria-label={t("notes.filters")}>
        {manyMaps && (
          <Select
            label={t("matches.filters.map")}
            value={query.map ?? ""}
            placeholder={t("demos.allMaps")}
            groups={[
              {
                options: [
                  any(t("demos.allMaps")),
                  ...options.maps.map((m) => ({
                    value: m.map,
                    label: `${m.mapNumber}. ${mapName(m.map)}`,
                    icon: isMapId(m.map) ? <img src={mapIconUrl(m.map)} alt="" width={18} height={18} /> : undefined,
                  })),
                ],
              },
            ]}
            onChange={(v) => set({ map: v })}
          />
        )}
        <div className="notes-rounds">
          <span className="muted small" id="notes-rounds">
            {t("notes.rounds")}
          </span>
          <label className="field">
            <span className="visually-hidden">{t("notes.roundFrom")}</span>
            <input
              className="mono"
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              value={query.round ?? ""}
              placeholder="1"
              onChange={(e) => setRounds(e.target.value, query.roundTo ? String(query.roundTo) : "")}
            />
          </label>
          <span className="muted" aria-hidden="true">
            –
          </span>
          <label className="field">
            <span className="visually-hidden">{t("notes.roundTo")}</span>
            <input
              className="mono"
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              value={query.roundTo ?? ""}
              placeholder={String(options.lastRound || "")}
              onChange={(e) => setRounds(query.round ? String(query.round) : "", e.target.value)}
            />
          </label>
        </div>
        <div className="segmented segmented-small" role="group" aria-label={t("notes.side")}>
          {(["", "t", "ct"] as const).map((s) => (
            <button key={s || "any"} type="button" className={`seg${s ? ` side-seg-${s}` : ""}`} aria-pressed={(query.side ?? "") === s} onClick={() => set({ side: s as NoteSide | "" })}>
              {s ? t(`live.sideShort.${s}`) : t("notes.anySide")}
            </button>
          ))}
        </div>
        {options.teams.length > 0 && (
          <Select
            label={t("notes.aboutWhichTeam")}
            value={query.team ?? ""}
            placeholder={t("notes.team")}
            groups={[{ options: [any(t("notes.anyTeam")), ...options.teams.map((n) => ({ value: n, label: n }))] }]}
            onChange={(v) => set({ team: v })}
          />
        )}
        {options.players.length > 0 && (
          <Select
            label={t("notes.aboutPlayer")}
            value={query.player ?? ""}
            placeholder={t("notes.player")}
            groups={[{ options: [any(t("notes.anyPlayer")), ...options.players.map((n) => ({ value: n, label: n }))] }]}
            onChange={(v) => set({ player: v })}
          />
        )}
        {options.authors.length > 1 && (
          <Select
            label={t("notes.byAuthor")}
            value={query.by ?? ""}
            placeholder={t("notes.author")}
            groups={[{ options: [any(t("notes.anyAuthor")), ...options.authors.map((a) => ({ value: a.id, label: a.name }))] }]}
            onChange={(v) => set({ by: v })}
          />
        )}
        <label className="match-search">
          <span className="visually-hidden">{t("notes.search")}</span>
          <Icon name="search" />
          <input type="search" value={q} placeholder={t("notes.search")} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>

      <p className="muted small" role="status">
        {filtered ? t("notes.matching", { count: data.total, all: data.all }) : t("notes.shown", { count: data.total })}
        {data.total > data.per && ` · ${first + 1}–${Math.min(data.total, first + data.per)}`}
        {filtered && (
          <>
            {" · "}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setQ("");
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              {t("mapView.clearFilters")}
            </button>
          </>
        )}
      </p>

      {data.notes.length === 0 && <p className="muted">{t("notes.noneMatch")}</p>}
      <ul className="notes-rows">
        {data.notes.map((n) => (
          <li key={n.id} className="notes-row">
            <Link to={noteUrl(n)} className="notes-row-go" aria-label={t("notes.open", { round: n.round + 1, text: n.text })}>
              <span className="notes-row-where mono small">
                {manyMaps && isMapId(n.map) && <img src={mapIconUrl(n.map)} alt="" width={16} height={16} />}
                {manyMaps && <span>{mapName(n.map)}</span>}
                <span className={n.endSeconds !== null ? "note-range" : undefined}>{t("notes.at", { n: n.round + 1, clock: n.seconds !== null ? noteWhen(n) : "·" })}</span>
              </span>
              <span className="notes-row-text">{n.text}</span>
              {n.pin && (
                <span className="chip chip-small match-note-pin">
                  <Icon name="target" size={12} />
                  {t("matches.notes.aSpot")}
                </span>
              )}
              <Icon name="play" size={14} />
            </Link>
            <NotePlayers players={n.players} />
            <div className="notes-row-meta">
              <NoteTags side={n.side} team={n.team} links={n.links} />
              {n.author && (
                <span className="match-note-author muted small">
                  <Avatar name={n.author.name} url={n.author.avatarUrl} size={18} />
                  {n.author.name}
                  {n.editedAt && <span> · {t("notes.edited")}</span>}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {pages > 1 && (
        <nav className="notes-pager" aria-label={t("notes.pages")}>
          <span className="muted small">{t("notes.pageOf", { page: data.page, pages })}</span>
          <div className="notes-pager-nums">
            <button type="button" className="btn btn-quiet btn-small" aria-label={t("notes.prevPage")} disabled={data.page <= 1} onClick={() => set({ page: String(data.page - 1) })}>
              <Icon name="chevronLeft" size={16} />
            </button>
            {pageNumbers(data.page, pages).map((n, i) =>
              n === null ? (
                <span key={`gap${i}`} className="muted" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  className={`btn btn-small ${n === data.page ? "btn-secondary" : "btn-quiet"}`}
                  aria-current={n === data.page ? "page" : undefined}
                  aria-label={t("notes.pageN", { n })}
                  onClick={() => set({ page: n === 1 ? undefined : String(n) })}
                >
                  {n}
                </button>
              ),
            )}
            <button type="button" className="btn btn-quiet btn-small" aria-label={t("notes.nextPage")} disabled={data.page >= pages} onClick={() => set({ page: String(data.page + 1) })}>
              <Icon name="chevronRight" size={16} />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
